/**
 * @sazeo/sdk — Performance Reviews hooks
 *
 * L82 Task: Hooks for review cycles, templates, manager reviews, 360 feedback.
 * All queries use Supabase directly with RLS enforcing tenant isolation.
 * Spec: specs/CODEX_TASK_L82_PERFORMANCE_REVIEWS.md
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// =============================================================================
// Types
// =============================================================================

export interface ReviewCycle {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  review_deadline: string;
  status: 'draft' | 'active' | 'closed' | 'archived';
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReviewTemplate {
  id: string;
  tenant_id: string;
  cycle_id: string;
  title: string;
  description: string | null;
  competencies: Array<{
    id: string;
    name: string;
    description: string;
    weight: number; // 0.0 - 1.0
  }>;
  goals_required: boolean;
  peer_feedback_enabled: boolean;
  direct_report_feedback_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface PerformanceReview {
  id: string;
  tenant_id: string;
  cycle_id: string;
  cycle?: { id?: string; name?: string } | null;
  employee_id: string;
  manager_id: string;
  reviewer_id?: string | null;
  template_id: string;
  status: ReviewStatus;
  overall_rating: number | null;
  competency_ratings: Record<string, number> | null;
  goals: string | null;
  manager_comments: string | null;
  summary?: string | null;
  strengths?: string | null;
  improvements?: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export type ReviewStatus =
  | 'pending'
  | 'in_progress'
  | 'submitted'
  | 'acknowledged'
  | 'draft'
  | 'in-progress'
  | 'reviewed'
  | 'finalized';

export interface ReviewFeedback {
  id: string;
  tenant_id: string;
  review_id: string;
  from_employee_id: string | null;
  feedback_type: 'peer' | 'direct_report' | 'self';
  competency_ratings: Record<string, number> | null;
  comments: string | null;
  submitted_at: string;
  created_at: string;
}

export interface CreateReviewCycleInput {
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  review_deadline: string;
}

export interface UpdateReviewCycleInput {
  name?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  review_deadline?: string;
  status?: ReviewCycle['status'];
}

export interface CreateReviewTemplateInput {
  cycle_id: string;
  title: string;
  description?: string;
  competencies: Array<{ id: string; name: string; description: string; weight: number }>;
  goals_required?: boolean;
  peer_feedback_enabled?: boolean;
  direct_report_feedback_enabled?: boolean;
}

export interface UpsertPerformanceReviewInput {
  cycle_id: string;
  employee_id: string;
  manager_id: string;
  template_id: string;
  overall_rating?: number;
  competency_ratings?: Record<string, number>;
  goals?: string;
  manager_comments?: string;
  status?: PerformanceReview['status'];
}

export interface SubmitReviewInput {
  review_id?: string;
  reviewId?: string;
  tenantId?: string;
  overall_rating?: number;
  summary?: string;
  strengths?: string | null;
  improvements?: string | null;
}

export interface AddReviewFeedbackInput {
  review_id: string;
  from_employee_id?: string | null;
  feedback_type: 'peer' | 'direct_report' | 'self';
  competency_ratings?: Record<string, number>;
  comments?: string;
}

export interface ArchiveReviewCycleInput {
  cycle_id: string;
}

// =============================================================================
// Query Keys
// =============================================================================

export const performanceKeys = {
  all: ['performance'] as const,
  cycles: (tenantId: string) => [...performanceKeys.all, 'cycles', tenantId] as const,
  cycle: (cycleId: string) => [...performanceKeys.all, 'cycle', cycleId] as const,
  templates: (tenantId: string) => [...performanceKeys.all, 'templates', tenantId] as const,
  template: (templateId: string) => [...performanceKeys.all, 'template', templateId] as const,
  reviewsForEmployee: (tenantId: string, employeeId: string) =>
    [...performanceKeys.all, 'reviewsForEmployee', tenantId, employeeId] as const,
  reviewsForManager: (tenantId: string, managerId: string) =>
    [...performanceKeys.all, 'reviewsForManager', tenantId, managerId] as const,
  review: (reviewId: string) => [...performanceKeys.all, 'review', reviewId] as const,
  feedback: (reviewId: string) => [...performanceKeys.all, 'feedback', reviewId] as const,
} as const;

// =============================================================================
// Hooks: Queries
// =============================================================================

export function useReviewCycles(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: performanceKeys.cycles(tenantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('review_cycles')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('start_date', { ascending: false });
      if (error) throw error;
      return data as ReviewCycle[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useReviewCycle(supabase: SupabaseClientLike, cycleId: string) {
  return useQuery({
    queryKey: performanceKeys.cycle(cycleId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('review_cycles')
        .select('*')
        .eq('id', cycleId)
        .single();
      if (error) throw error;
      return data as ReviewCycle;
    },
    enabled: Boolean(cycleId),
  });
}

export function useReviewTemplates(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: performanceKeys.templates(tenantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('review_templates')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as ReviewTemplate[];
    },
    enabled: Boolean(tenantId),
  });
}

export function usePerformanceReviewsForEmployee(
  supabase: SupabaseClientLike,
  tenantId: string,
  employeeId: string,
) {
  return useQuery({
    queryKey: performanceKeys.reviewsForEmployee(tenantId, employeeId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('performance_reviews')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('employee_id', employeeId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PerformanceReview[];
    },
    enabled: Boolean(tenantId && employeeId),
  });
}

export function usePerformanceReviewsForManager(
  supabase: SupabaseClientLike,
  tenantId: string,
  managerId: string,
) {
  return useQuery({
    queryKey: performanceKeys.reviewsForManager(tenantId, managerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('performance_reviews')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('manager_id', managerId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PerformanceReview[];
    },
    enabled: Boolean(tenantId && managerId),
  });
}

export function usePerformanceReview(supabase: SupabaseClientLike, reviewId: string) {
  return useQuery({
    queryKey: performanceKeys.review(reviewId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('performance_reviews')
        .select('*')
        .eq('id', reviewId)
        .single();
      if (error) throw error;
      return data as PerformanceReview;
    },
    enabled: Boolean(reviewId),
  });
}

export function useReviewFeedback(supabase: SupabaseClientLike, reviewId: string) {
  return useQuery({
    queryKey: performanceKeys.feedback(reviewId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('review_feedback')
        .select('*')
        .eq('review_id', reviewId)
        .order('submitted_at', { ascending: false });
      if (error) throw error;
      return data as ReviewFeedback[];
    },
    enabled: Boolean(reviewId),
  });
}

// =============================================================================
// Hooks: Mutations
// =============================================================================

export function useCreateReviewCycle(supabase: SupabaseClientLike, tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateReviewCycleInput) => {
      const { data, error } = await supabase
        .from('review_cycles')
        .insert({
          tenant_id: tenantId,
          ...input,
        })
        .select()
        .single();
      if (error) throw error;
      return data as ReviewCycle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceKeys.cycles(tenantId) });
    },
  });
}

export function useUpdateReviewCycle(supabase: SupabaseClientLike, tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ cycleId, ...input }: UpdateReviewCycleInput & { cycleId: string }) => {
      const { data, error } = await supabase
        .from('review_cycles')
        .update(input)
        .eq('id', cycleId)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as ReviewCycle;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: performanceKeys.cycles(tenantId) });
      queryClient.invalidateQueries({ queryKey: performanceKeys.cycle(data.id) });
    },
  });
}

export function useCreateReviewTemplate(supabase: SupabaseClientLike, tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateReviewTemplateInput) => {
      const { data, error } = await supabase
        .from('review_templates')
        .insert({
          tenant_id: tenantId,
          ...input,
        })
        .select()
        .single();
      if (error) throw error;
      return data as ReviewTemplate;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: performanceKeys.templates(tenantId) });
      queryClient.invalidateQueries({ queryKey: performanceKeys.cycle(data.cycle_id) });
    },
  });
}

export function useUpsertPerformanceReview(supabase: SupabaseClientLike, tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertPerformanceReviewInput & { reviewId?: string }) => {
      if (input.reviewId) {
        // Update existing
        const { reviewId, ...updateData } = input;
        const { data, error } = await supabase
          .from('performance_reviews')
          .update(updateData)
          .eq('id', reviewId)
          .eq('tenant_id', tenantId)
          .select()
          .single();
        if (error) throw error;
        return data as PerformanceReview;
      }
      // Insert new
      const { data, error } = await supabase
        .from('performance_reviews')
        .insert({
          tenant_id: tenantId,
          ...input,
        })
        .select()
        .single();
      if (error) throw error;
      return data as PerformanceReview;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: performanceKeys.reviewsForEmployee(tenantId, data.employee_id),
      });
      queryClient.invalidateQueries({
        queryKey: performanceKeys.reviewsForManager(tenantId, data.manager_id),
      });
      queryClient.invalidateQueries({ queryKey: performanceKeys.review(data.id) });
    },
  });
}

export function useSubmitReview(supabase: SupabaseClientLike, tenantId = '') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SubmitReviewInput) => {
      const reviewId = input.review_id ?? input.reviewId;
      const resolvedTenantId = input.tenantId ?? tenantId;
      if (!reviewId) throw new Error('review_id is required');
      const { data, error } = await supabase
        .from('performance_reviews')
        .update({
          status: 'submitted',
          submitted_at: new Date().toISOString(),
          overall_rating: input.overall_rating,
          summary: input.summary,
          strengths: input.strengths,
          improvements: input.improvements,
        })
        .eq('id', reviewId)
        .eq('tenant_id', resolvedTenantId)
        .select()
        .single();
      if (error) throw error;
      return data as PerformanceReview;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: performanceKeys.reviewsForEmployee(tenantId, data.employee_id),
      });
      queryClient.invalidateQueries({
        queryKey: performanceKeys.reviewsForManager(tenantId, data.manager_id),
      });
      queryClient.invalidateQueries({ queryKey: performanceKeys.review(data.id) });
    },
  });
}

export function useMyReviews(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: performanceKeys.reviewsForEmployee(tenantId, 'me'),
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Authenticated user is required');

      const { data, error } = await supabase
        .from('performance_reviews')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('employee_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PerformanceReview[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useReviewsToComplete(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: performanceKeys.reviewsForManager(tenantId, 'me'),
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Authenticated user is required');

      const { data, error } = await supabase
        .from('performance_reviews')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('manager_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PerformanceReview[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useAddReviewFeedback(supabase: SupabaseClientLike, tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddReviewFeedbackInput) => {
      const fromEmployeeId =
        input.from_employee_id === undefined
          ? (await supabase.auth.getUser()).data.user?.id
          : input.from_employee_id;
      if (fromEmployeeId === undefined) throw new Error('Authenticated user is required');

      const { data, error } = await supabase
        .from('review_feedback')
        .insert({
          tenant_id: tenantId,
          ...input,
          from_employee_id: fromEmployeeId,
        })
        .select()
        .single();
      if (error) throw error;
      return data as ReviewFeedback;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: performanceKeys.feedback(data.review_id) });
      queryClient.invalidateQueries({ queryKey: performanceKeys.review(data.review_id) });
    },
  });
}

export function useArchiveReviewCycle(supabase: SupabaseClientLike, tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ArchiveReviewCycleInput) => {
      const { data, error } = await supabase
        .from('review_cycles')
        .update({
          status: 'archived',
        })
        .eq('id', input.cycle_id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as ReviewCycle;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: performanceKeys.cycles(tenantId) });
      queryClient.invalidateQueries({ queryKey: performanceKeys.cycle(data.id) });
    },
  });
}
